import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import {
  ArrowBack,
  Autorenew,
  PlayArrow,
  Replay,
  ReportProblemOutlined,
  Visibility,
} from "@material-ui/icons";
import {
  CustomTable,
  ModalError,
  ModalSuccess,
  SearchTextInput,
  SkeletonComponent,
  StatusChip,
} from "components";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { getErrors } from "utils";
import {
  confirmFinishDSRFProcess,
  getDSRFMonitoringDatatable,
  getDSRFMonitoringDetail,
  restartDSRFProcess,
  resumeDSRFProcess,
  retryFailedDSRFBatches,
} from "utils/DSRFService";

const statusCondition = {
  F: "success",
  FINISH: "success",
  FINISHED: "success",
  SUCCESS: "success",
  E: "danger",
  ERROR: "danger",
  FAILED: "danger",
  NR: "warning",
  "NEED REVIEW": "warning",
  NEED_REVIEW: "warning",
  R: "warning",
  RUNNING: "warning",
  "IN PROGRESS": "warning",
  IN_PROGRESS: "warning",
  "ON PROGRESS": "warning",
  ON_PROGRESS: "warning",
  Q: "active",
  QUEUE: "active",
  QUEUED: "active",
  "IN QUEUE": "active",
  IN_QUEUE: "active",
};

const statusLabel = {
  F: "Finish",
  FINISH: "Finish",
  FINISHED: "Finish",
  SUCCESS: "Finish",
  E: "Error",
  ERROR: "Error",
  FAILED: "Error",
  NR: "Need Review",
  "NEED REVIEW": "Need Review",
  NEED_REVIEW: "Need Review",
  R: "In Progress",
  RUNNING: "In Progress",
  "IN PROGRESS": "In Progress",
  IN_PROGRESS: "In Progress",
  "ON PROGRESS": "In Progress",
  ON_PROGRESS: "In Progress",
  Q: "Queued",
  QUEUE: "Queued",
  QUEUED: "Queued",
  "IN QUEUE": "Queued",
  IN_QUEUE: "Queued",
};



const useStyles = makeStyles(() => ({
  actionButton: {
    backgroundColor: "#111827",
    color: "white",
    borderRadius: 4,
    width: 32,
    height: 32,
    "&:hover": {
      backgroundColor: "#1f2937",
    },
    "&.Mui-disabled": {
      backgroundColor: "#9CA3AF",
      color: "#FFFFFF",
      opacity: 1,
    },
  },
  actionButtonWarning: {
    backgroundColor: "#D97706",
    color: "white",
    borderRadius: 4,
    "&:hover": {
      backgroundColor: "#B45309",
    },
  },
  dialogTitle: {
    paddingBottom: 0,
  },
  backButton: {
    marginRight: 8,
    color: "#111827",
  },
  filenameText: {
    wordBreak: "break-all",
  },
  summaryCard: {
    height: "100%",
    borderRadius: 8,
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  tableWrapper: {
    width: "100%",
  },
  divider: {
    borderTop: "1px solid #E5E7EB",
    marginTop: 16,
    marginBottom: 16,
  },
  summaryContainer: {
    border: "1px solid #D1D5DC",
    borderRadius: 8,
    padding: 16,
  },
  btnSecondary: {
    borderColor: "#D1D5DC",
    color: "#374151",
    textTransform: "none",
    fontWeight: 600,
  },
  btnPrimary: {
    backgroundColor: "#111827",
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#1F2937",
    },
  },
  btnWarning: {
    backgroundColor: "#D97706",
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 6,
    "&:hover": {
      backgroundColor: "#B45309",
    },
  },
  btnDanger: {
    backgroundColor: "#DC2626",
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 6,
    "&:hover": {
      backgroundColor: "#B91C1C",
    },
  },
  btnDark: {
    backgroundColor: "#111827",
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 6,
    "&:hover": {
      backgroundColor: "#1F2937",
    },
  },
  progressBarTrack: {
    width: "100%",
    height: 8,
    backgroundColor: "#E5E7EB",
    borderRadius: 4,
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    backgroundColor: "#6B7280",
    borderRadius: 4,
  },
  warningIcon: {
    color: "#111827",
    fontSize: 22,
    marginTop: 1,
  },
}));

function ComponentReportProcessMonitoring() {
  const classes = useStyles();
  const [loadingPageSkeleton, setLoadingPageSkeleton] = useState(false);
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [stateReportMonitor, setStateReportMonitor] = useState({
    data: [],
  });
  const [openDetail, setOpenDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  console.log({ selectedRecord });
  const { data } = stateReportMonitor;

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getDetail = async (processId, record) => {
    try {
      const res = await getDSRFMonitoringDetail(processId);
      const resData =
        res?.data?.data?.data || res?.data?.data || res?.data || {};

      const info = {
        id:
          resData?.process_id ||
          resData?.id ||
          resData?.header_id ||
          resData?.info?.id ||
          processId ||
          "-",
        dsp:
          resData?.dsp_name ||
          resData?.dsp ||
          resData?.info?.dsp ||
          record?.dsp_name ||
          "-",
        generatedDate:
          resData?.start_time ||
          resData?.generated_date ||
          resData?.created_at ||
          resData?.info?.generatedDate ||
          record?.date ||
          record?.start_time ||
          "-",
        filename:
          resData?.filename ||
          resData?.file_name ||
          resData?.info?.filename ||
          "-",
        periodDate:
          resData?.period && String(resData.period).trim() !== ""
            ? resData.period
            : resData?.start_date &&
              resData?.end_date &&
              String(resData.start_date).trim() !== ""
            ? `${resData.start_date} - ${resData.end_date}`
            : record?.period || "-",
        status:
          resData?.status || resData?.info?.status || record?.status || "-",
        canRetry: Boolean(
          record?.can_retry !== undefined
            ? record.can_retry
            : record?.canRetry !== undefined
            ? record.canRetry
            : resData?.can_retry !== undefined
            ? resData.can_retry
            : resData?.canRetry !== undefined
            ? resData.canRetry
            : false
        ),
        can_retry: Boolean(
          record?.can_retry !== undefined
            ? record.can_retry
            : record?.canRetry !== undefined
            ? record.canRetry
            : resData?.can_retry !== undefined
            ? resData.can_retry
            : resData?.canRetry !== undefined
            ? resData.canRetry
            : false
        ),
        progress:
          resData?.progress !== undefined
            ? Number(resData.progress)
            : record?.progress !== undefined
            ? Number(record.progress)
            : 100,
      };

      const rs =
        resData?.result_summary ||
        resData?.summary_result ||
        resData?.data?.result_summary ||
        {};
      const totalData = rs?.total_data || rs?.total || {};
      const totalProcessed = rs?.total_processed || rs?.processed || {};
      const totalSkipped = rs?.total_skipped || rs?.skipped || {};
      const totalMatched = rs?.total_matched || rs?.matched || {};

      const formatNumber = num => {
        if (num === null || num === undefined || num === "") return "0";
        if (
          typeof num === "string" &&
          isNaN(Number(num.replace(/[^0-9.-]+/g, "")))
        )
          return num;
        const parsed = Number(String(num).replace(/[^0-9.-]+/g, ""));
        return isNaN(parsed) ? num : parsed.toLocaleString("en-US");
      };

      const formatCurrency = val => {
        if (val === null || val === undefined || val === "") return "Rp0.00";
        if (typeof val === "string" && val.startsWith("Rp")) return val;
        const parsed = Number(String(val).replace(/[^0-9.-]+/g, ""));
        if (isNaN(parsed)) return val;
        return `Rp${parsed.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`;
      };

      const summaryData = resData?.summary || [
        {
          title: "Total DSRF Data",
          revenue: formatCurrency(
            totalData?.base_revenue ??
              totalData?.total_revenue ??
              totalData?.revenue ??
              resData?.base_revenue ??
              resData?.total_revenue ??
              resData?.revenue ??
              0
          ),
          traffic: formatNumber(
            totalData?.total_traffic ??
              totalData?.traffic ??
              resData?.total_traffic ??
              resData?.traffic ??
              0
          ),
          row: `${formatNumber(
            totalData?.rows ??
              totalData?.row ??
              resData?.total_row ??
              resData?.total_rows ??
              resData?.total_songs ??
              0
          )} Row`,
          records: null,
          bgColor: "#FFFFFF",
          borderColor: "#D1D5DC",
        },
        {
          title: "Total DSRF Processed",
          revenue: formatCurrency(
            totalProcessed?.base_revenue ??
              totalProcessed?.total_revenue ??
              totalProcessed?.revenue ??
              resData?.base_revenue ??
              resData?.processed_revenue ??
              resData?.total_processed_revenue ??
              0
          ),
          traffic: formatNumber(
            totalProcessed?.total_traffic ??
              totalProcessed?.traffic ??
              resData?.processed_traffic ??
              resData?.total_processed_traffic ??
              0
          ),
          row: `${formatNumber(
            totalProcessed?.rows ??
              totalProcessed?.row ??
              resData?.processed_row ??
              resData?.processed_rows ??
              resData?.processed_songs ??
              0
          )} Row`,
          records:
            totalProcessed?.records !== undefined ||
            resData?.finish_count !== undefined
              ? `${formatNumber(
                  totalProcessed?.records ?? resData?.finish_count ?? 0
                )} Records`
              : null,
          bgColor: "#F3F4F6",
          borderColor: "#D1D5DC",
        },
        {
          title: "Total DSRF Data Skipped",
          revenue: formatCurrency(
            totalSkipped?.base_revenue ??
              totalSkipped?.total_revenue ??
              totalSkipped?.revenue ??
              resData?.base_revenue ??
              resData?.skipped_revenue ??
              0
          ),
          traffic: formatNumber(
            totalSkipped?.total_traffic ??
              totalSkipped?.traffic ??
              resData?.skipped_traffic ??
              0
          ),
          row: `${formatNumber(
            totalSkipped?.rows ?? totalSkipped?.row ?? resData?.skipped_row ?? 0
          )} Row`,
          records: null,
          bgColor: "#FEF2F2",
          borderColor: "#FECACA",
        },
        {
          title: "Total DSRF Matched",
          revenue: formatCurrency(
            totalMatched?.base_revenue ??
              totalMatched?.total_revenue ??
              totalMatched?.revenue ??
              resData?.base_revenue ??
              resData?.matched_revenue ??
              0
          ),
          traffic: formatNumber(
            totalMatched?.total_traffic ??
              totalMatched?.traffic ??
              resData?.matched_traffic ??
              0
          ),
          row: `${formatNumber(
            totalMatched?.rows ??
              totalMatched?.row ??
              totalMatched?.row_count ??
              0
          )} Row`,
          records: `${formatNumber(
            totalMatched?.records || resData?.claimed_record_count || 0
          )} Records`,
          bgColor: "#F0FDF4",
          borderColor: "#BBF7D0",
        },
      ];

      setDetailData({
        info,
        summary: summaryData,
        raw: resData,
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingDetail(false);
    }
  };

  const getDsrf = async () => {
    setLoadingPageSkeleton(true);
    try {
      const res = await getDSRFMonitoringDatatable({
        page: queryParams?.page || 1,
        size: queryParams?.size || 10,
        search: queryParams?.search || "",
      });

      const raw = res?.data?.data?.data || res?.data?.data || [];
      const total =
        res?.data?.meta?.total ||
        res?.data?.total ||
        res?.data?.data?.total ||
        raw.length;

      setStateReportMonitor({
        data: raw,
      });
      const pageCount = getTotalPage(total, queryParams?.size || 10);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
      setStateReportMonitor({ data: [] });
      setTableTotalPage(1);
    } finally {
      setLoadingPageSkeleton(false);
    }
  };

  const debounceGetDsrf = useCallback(
    debounce(() => {
      getDsrf();
    }, 500),
    [queryParams]
  );

  useEffect(() => {
    if (queryParams?.search) {
      debounceGetDsrf();
      return () => {
        debounceGetDsrf.cancel();
      };
    }
    getDsrf();
  }, [queryParams?.page, queryParams?.size, queryParams?.search]);

  useEffect(() => {
    let intervalId = null;

    const processId =
      selectedRecord?.process_id ||
      selectedRecord?.header_id ||
      selectedRecord?.id;

    const normStatus = String(
      detailData?.info?.status || selectedRecord?.status || ""
    ).toUpperCase();

    const isInProgress = [
      "R",
      "RUNNING",
      "IN PROGRESS",
      "IN_PROGRESS",
      "ON PROGRESS",
      "ON_PROGRESS",
    ].includes(normStatus);

    if (openDetail && processId && isInProgress) {
      intervalId = setInterval(() => {
        getDetail(processId, selectedRecord);
      }, 3000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [openDetail, selectedRecord, detailData?.info?.status]);

  const handleOpenDetail = async record => {
    setSelectedRecord(record);
    setOpenDetail(true);
    setLoadingDetail(true);
    const processId = record?.process_id || record?.header_id || record?.id;
    getDetail(processId, record);
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    getDsrf();
  };

  const handleRetryFailedBatches = async record => {
    const target = record || selectedRecord;
    const processId =
      target?.process_id ||
      detailData?.raw?.process_id ||
      detailData?.info?.id ||
      target?.header_id ||
      target?.id;

    if (!processId) {
      ModalError("Process ID is required");
      return;
    }

    setActionLoading(true);
    try {
      const res = await retryFailedDSRFBatches({
        process_id: Number(processId) || processId,
      });
      ModalSuccess(
        res?.data?.message || "Retry failed batches initiated successfully."
      );
      getDsrf();
      if (openDetail && processId) {
        handleOpenDetail(target);
      }
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestartProcess = async record => {
    const target = record || selectedRecord;
    const processId = target?.process_id || target?.header_id || target?.id;
    setActionLoading(true);
    try {
      const res = await restartDSRFProcess({
        process_id: processId,
        header_id: target?.header_id || processId,
      });
      ModalSuccess(
        res?.data?.message || "DSRF process restarted successfully."
      );
      getDsrf();
      if (openDetail && processId) {
        handleOpenDetail(target);
      }
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmFinish = async record => {
    const target = record || selectedRecord;
    const processId = target?.process_id || target?.header_id || target?.id;
    setActionLoading(true);
    try {
      const res = await confirmFinishDSRFProcess({
        process_id: processId,
        header_id: target?.header_id || processId,
      });
      ModalSuccess(
        res?.data?.message || "DSRF process confirmed finish successfully."
      );
      getDsrf();
      if (openDetail && processId) {
        handleOpenDetail(target);
      }
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeProcess = async record => {
    const target = record || selectedRecord;
    const processId = target?.process_id || target?.header_id || target?.id;
    setActionLoading(true);
    try {
      const res = await resumeDSRFProcess({
        process_id: processId,
        header_id: target?.header_id || processId,
      });
      ModalSuccess(
        res?.data?.message || "Resume process initiated successfully."
      );
      getDsrf();
      if (openDetail && processId) {
        handleOpenDetail(target);
      }
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setActionLoading(false);
    }
  };

  const columnReportMonitoring = [
    {
      name: "all",
      title: "Action",
      renderText: record => {
        const isQueued = [
          "Q",
          "QUEUE",
          "QUEUED",
          "IN QUEUE",
          "IN_QUEUE",
        ].includes(String(record?.status || "").toUpperCase());

        return (
          <Tooltip title={isQueued ? "Queued" : "View Detail"}>
            <span>
              <IconButton
                size="small"
                className={classes.actionButton}
                onClick={() => handleOpenDetail(record)}
                disabled={isQueued}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    {
      name: "process_id",
      title: "ID Process",
      renderText: processId => processId || "-",
    },
    {
      name: "date",
      title: "Date",
      renderText: (date, record) => {
        const raw = date || record?.start_time;
        if (!raw) return "-";
        const parts = String(raw).split(" ");
        const datePart = parts[0] || raw;
        const timePart = parts[1] || "";
        return (
          <Box>
            <Box>{datePart}</Box>
            {timePart && (
              <Box fontSize={12} color="#6B7280">
                {timePart}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      name: "dsp_name",
      title: "DSP",
      renderText: dspName => dspName || "-",
    },
    {
      name: "period",
      title: "Periode",
      renderText: period => period || "-",
    },
    {
      name: "status",
      title: "Status",
      renderText: status => {
        const normalized = String(status || "").toUpperCase();
        return (
          <StatusChip
            type={statusCondition[normalized] || "default"}
            label={statusLabel[normalized] || status || "-"}
          />
        );
      },
    },
  ];

  return (
    <Grid container>
      <Box className={classes.tableWrapper}>
        <Box className={classes.topBar}>
          <Box width={300}>
            <SearchTextInput
              placeholder="Search ID, DSP, or status..."
              value={queryParams?.search}
              onChange={event =>
                handleChangeQueryParams(event?.target?.value, "search")
              }
            />
          </Box>
          <Button
            variant="outlined"
            className={classes.btnSecondary}
            startIcon={<Autorenew />}
            onClick={getDsrf}
            disabled={loadingPageSkeleton}
          >
            Refresh
          </Button>
        </Box>

        {loadingPageSkeleton ? (
          <SkeletonComponent variant="wave" />
        ) : (
          <CustomTable
            columnTable={columnReportMonitoring}
            isLoading={loadingPageSkeleton}
            data={data || []}
            page={queryParams?.page}
            pageSize={queryParams?.size}
            pageCount={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangePageSize={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
          />
        )}
      </Box>

      <Dialog
        open={openDetail}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Grid container alignItems="center">
            <Grid item>
              <IconButton
                onClick={handleCloseDetail}
                size="small"
                className={classes.backButton}
              >
                <ArrowBack />
              </IconButton>
            </Grid>
            <Grid item>
              <Box fontSize={20} fontWeight={700}>
                Detail DSRF Process
              </Box>
            </Grid>
          </Grid>
          <Box fontSize={14} color="#6B7280" mt={0.5} ml={5.5}>
            This is detail information of DSRF Process
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box className={classes.divider} />

          {loadingDetail ? (
            <SkeletonComponent variant="wave" />
          ) : (
            <Box mb={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box mb={1.5}>
                    <Box fontSize={14} fontWeight={700} color="#111827">
                      DSRF Process ID
                    </Box>
                    <Box fontSize={14} mt={0.5}>
                      {detailData?.info?.id || "-"}
                    </Box>
                  </Box>
                  <Box mb={1.5}>
                    <Box fontSize={14} fontWeight={700} color="#111827">
                      Generated Date
                    </Box>
                    <Box fontSize={14} mt={0.5}>
                      {detailData?.info?.generatedDate || "-"}
                    </Box>
                  </Box>
                  <Box>
                    <Box fontSize={14} fontWeight={700} color="#111827">
                      Period Date
                    </Box>
                    <Box fontSize={14} mt={0.5}>
                      {detailData?.info?.periodDate || "-"}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box mb={1.5}>
                    <Box fontSize={14} fontWeight={700} color="#111827">
                      DSP
                    </Box>
                    <Box fontSize={14} mt={0.5}>
                      {detailData?.info?.dsp || "-"}
                    </Box>
                  </Box>
                  <Box mb={1.5}>
                    <Box fontSize={14} fontWeight={700} color="#111827">
                      Filename
                    </Box>
                    <Box
                      fontSize={14}
                      mt={0.5}
                      className={classes.filenameText}
                    >
                      {detailData?.info?.filename || "-"}
                    </Box>
                  </Box>
                  <Box>
                    <Box
                      fontSize={14}
                      fontWeight={700}
                      color="#111827"
                      mb={0.5}
                    >
                      Status
                    </Box>
                    <StatusChip
                      isExporting
                      type={
                        statusCondition[
                          String(detailData?.info?.status || "").toUpperCase()
                        ] || "default"
                      }
                      label={
                        statusLabel[
                          String(detailData?.info?.status || "").toUpperCase()
                        ] ||
                        detailData?.info?.status ||
                        "-"
                      }
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box className={classes.divider} />

          {(() => {
            const normStatus = String(
              detailData?.info?.status || ""
            ).toUpperCase();
            const isError = ["E", "ERROR", "FAILED"].includes(normStatus);
            const isInProgress = [
              "R",
              "RUNNING",
              "IN PROGRESS",
              "IN_PROGRESS",
              "ON PROGRESS",
              "ON_PROGRESS",
            ].includes(normStatus);
            const canRetry = Boolean(
              detailData?.info?.canRetry !== undefined
                ? detailData.info.canRetry
                : detailData?.info?.can_retry !== undefined
                ? detailData.info.can_retry
                : selectedRecord?.can_retry !== undefined
                ? selectedRecord.can_retry
                : false
            );
            const isNeedReview = ["NR", "NEED REVIEW", "NEED_REVIEW"].includes(
              normStatus
            );
            const isStoppedMidway =
              canRetry &&
              !isNeedReview &&
              detailData?.info?.progress !== undefined &&
              detailData?.info?.progress < 100;

            if (isInProgress) {
              return (
                <Box mb={2}>
                  <Box fontSize={16} fontWeight={700} color="#111827" mb={1.5}>
                    DSRF Process Progress
                  </Box>
                  <Box mb={0.5} fontSize={13} color="#9CA3AF" fontWeight={500}>
                    {detailData?.info?.progress || 0}%
                  </Box>
                  <Box className={classes.progressBarTrack}>
                    <Box
                      className={classes.progressBarFill}
                      width={`${Math.min(
                        Math.max(detailData?.info?.progress || 0, 0),
                        100
                      )}%`}
                    />
                  </Box>
                </Box>
              );
            }

            if (isStoppedMidway) {
              return (
                <Box mb={2}>
                  <Box fontSize={16} fontWeight={700} color="#111827" mb={1.5}>
                    DSRF Process Progress
                  </Box>
                  <Box mb={0.5} fontSize={13} color="#9CA3AF" fontWeight={500}>
                    {detailData?.info?.progress}%
                  </Box>
                  <Box className={classes.progressBarTrack}>
                    <Box
                      className={classes.progressBarFill}
                      width={`${Math.min(
                        Math.max(detailData?.info?.progress, 0),
                        100
                      )}%`}
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    gridGap={10}
                    mt={2.5}
                  >
                    <ReportProblemOutlined className={classes.warningIcon} />
                    <Box>
                      <Box
                        fontSize={15}
                        fontWeight={700}
                        color="#111827"
                        lineHeight="1.3"
                      >
                        DSRF Process Stopped Midway
                      </Box>
                      <Box fontSize={13} color="#6B7280" mt={0.5}>
                        The last saved progress was at{" "}
                        {detailData?.info?.progress}%. The process can resume
                        without starting over.
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            }

            if (isError && !canRetry) {
              return (
                <Box display="flex" alignItems="flex-start" gridGap={10} my={2}>
                  <ReportProblemOutlined className={classes.warningIcon} />
                  <Box>
                    <Box
                      fontSize={16}
                      fontWeight={700}
                      color="#111827"
                      lineHeight="1.3"
                    >
                      DSRF Process Failed
                    </Box>
                    <Box fontSize={14} color="#6B7280" mt={0.5}>
                      DSRF Process completely failed and no progress was saved.
                      The process need to be restarted.
                    </Box>
                  </Box>
                </Box>
              );
            }

            return (
              <Box mb={2}>
                <Box fontSize={16} fontWeight={700} color="#111827" mb={1.5}>
                  Result Summary
                </Box>
                <Box className={classes.summaryContainer}>
                  <Grid container spacing={2}>
                    {detailData?.summary?.map((card, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Box
                          p={1.5}
                          bgcolor={card.bgColor || "#F9FAFB"}
                          border={`1px solid ${card.borderColor || "#D1D5DC"}`}
                          className={classes.summaryCard}
                        >
                          <Box
                            fontSize={14}
                            fontWeight={700}
                            color="#111827"
                            mb={1.5}
                          >
                            {card.title}
                          </Box>

                          <Box fontSize={13} color="#6B7280" mb={0.5}>
                            Total Revenue
                          </Box>
                          <Box
                            fontSize={15}
                            fontWeight={600}
                            color="#111827"
                            mb={1}
                          >
                            {card.revenue}
                          </Box>

                          <Box fontSize={13} color="#6B7280" mb={0.5}>
                            Total Traffic
                          </Box>
                          <Box
                            fontSize={15}
                            fontWeight={600}
                            color="#111827"
                            mb={1.5}
                          >
                            {card.traffic}
                          </Box>

                          {card.row && (
                            <Box
                              fontSize={12}
                              color="#6B7280"
                              mb={card.records ? 0.5 : 0}
                            >
                              {card.row}
                            </Box>
                          )}
                          {card.records && (
                            <Box fontSize={12} color="#6B7280">
                              {card.records}
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        {(() => {
          const normStatus = String(
            detailData?.info?.status || ""
          ).toUpperCase();
          const isNeedReview = ["NR", "NEED REVIEW", "NEED_REVIEW"].includes(
            normStatus
          );
          const isFinished = ["F", "FINISH", "FINISHED", "SUCCESS"].includes(
            normStatus
          );
          const isInProgress = [
            "R",
            "RUNNING",
            "IN PROGRESS",
            "IN_PROGRESS",
            "ON PROGRESS",
            "ON_PROGRESS",
          ].includes(normStatus);
          const isError = ["E", "ERROR", "FAILED"].includes(normStatus);
          const canRetry = Boolean(
            detailData?.info?.canRetry !== undefined
              ? detailData.info.canRetry
              : detailData?.info?.can_retry !== undefined
              ? detailData.info.can_retry
              : selectedRecord?.can_retry !== undefined
              ? selectedRecord.can_retry
              : false
          );
          const isStoppedMidway =
            canRetry &&
            !isNeedReview &&
            detailData?.info?.progress !== undefined &&
            detailData?.info?.progress < 100;

          if (isInProgress) return null;
          if (isFinished && !canRetry) return null;

          if (isNeedReview) {
            return (
              <DialogActions>
                <Box display="flex" gridGap={12} p={1}>
                  {canRetry ? (
                    <Button
                      variant="contained"
                      className={classes.btnWarning}
                      startIcon={
                        actionLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Replay />
                        )
                      }
                      onClick={() => handleRetryFailedBatches()}
                      disabled={actionLoading}
                    >
                      Retry Failed Batch
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      className={classes.btnDanger}
                      startIcon={
                        actionLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Autorenew />
                        )
                      }
                      onClick={() => handleRestartProcess()}
                      disabled={actionLoading}
                    >
                      Restart Process
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    className={classes.btnDark}
                    onClick={() => handleConfirmFinish()}
                    disabled={actionLoading}
                  >
                    Confirm Finish
                  </Button>
                </Box>
              </DialogActions>
            );
          }

          if (isStoppedMidway) {
            return (
              <DialogActions>
                <Box display="flex" gridGap={12} p={1}>
                  <Button
                    variant="contained"
                    className={classes.btnDanger}
                    startIcon={
                      actionLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Autorenew />
                      )
                    }
                    onClick={() => handleRestartProcess()}
                    disabled={actionLoading}
                  >
                    Restart Process
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.btnDark}
                    startIcon={
                      actionLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PlayArrow />
                      )
                    }
                    onClick={() => handleResumeProcess()}
                    disabled={actionLoading}
                  >
                    Resume Process
                  </Button>
                </Box>
              </DialogActions>
            );
          }

          if (isError && !canRetry) {
            return (
              <DialogActions>
                <Box display="flex" p={1}>
                  <Button
                    variant="contained"
                    className={classes.btnDanger}
                    startIcon={
                      actionLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Autorenew />
                      )
                    }
                    onClick={() => handleRestartProcess()}
                    disabled={actionLoading}
                  >
                    Restart Process
                  </Button>
                </Box>
              </DialogActions>
            );
          }

          if (canRetry) {
            return (
              <DialogActions>
                <Box display="flex" gridGap={12} p={1}>
                  <Button
                    variant="contained"
                    className={classes.btnWarning}
                    startIcon={
                      actionLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Replay />
                      )
                    }
                    onClick={() => handleRetryFailedBatches()}
                    disabled={actionLoading}
                  >
                    Retry Failed Batch
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.btnDark}
                    onClick={() => handleConfirmFinish()}
                    disabled={actionLoading}
                  >
                    Confirm Finish
                  </Button>
                </Box>
              </DialogActions>
            );
          }

          return null;
        })()}
      </Dialog>
    </Grid>
  );
}

export default ComponentReportProcessMonitoring;
